import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { clearPublicSettingsCache } from "../../hooks/usePublicSettings";
import { Save, Check, Plus, X, Mail, Loader2, ShieldCheck } from "lucide-react";

const AUDIT_DOT = {
  healthy: "bg-emerald-500",
  warning: "bg-amber-500",
  critical: "bg-red-500",
};
const AUDIT_TEXT = {
  healthy: "Healthy",
  warning: "Warning",
  critical: "Critical",
};
import SeasonsSection from "../../components/admin/SeasonsSection";
import UniversalWidgetModal from "../../components/admin/UniversalWidgetModal";
import EmailTemplatesSection from "../../components/admin/EmailTemplatesSection";

export default function AdminBusiness() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [embedOpen, setEmbedOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [sendingAudit, setSendingAudit] = useState(false);

  const sendAuditReport = async () => {
    setSendingAudit(true);
    try {
      const { data } = await api.post("/admin/security-audit/send");
      setAudit(data);
      if (data.report_sent_to?.length) toast.success(`Report sent to ${data.report_sent_to.join(", ")}`);
      else toast.error("Report could not be delivered — check your SMTP settings.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSendingAudit(false);
    }
  };

  const runAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.post("/admin/security-audit");
      setAudit(data);
      if (data.overall === "healthy") toast.success("All checks healthy");
      else if (data.overall === "warning") toast.warning(`${data.counts.warning} warning(s) found`);
      else toast.error(`${data.counts.critical} critical issue(s) found`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAuditing(false);
    }
  };

  const sendTestEmail = async () => {
    const to = testEmail.trim();
    if (!to) { toast.error("Enter a recipient address first."); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/test-email", { to });
      if (data?.ok) toast.success(`Test email sent via ${data.transport.toUpperCase()} to ${to}`);
      else if (data?.status === "mock_no_key") toast.error("No transport configured — set an SMTP host (or RESEND_API_KEY) first.");
      else toast.error(`Send failed: ${data?.status || "unknown error"}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/settings")
      .then((r) => { if (!cancelled) setSettings(r.data); })
      .catch((e) => { if (!cancelled) setError(formatApiError(e.response?.data?.detail) || e.message); });
    return () => { cancelled = true; };
  }, []);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    const payload = { ...settings };
    delete payload.id;
    delete payload._id;
    // Coerce numbers
    ["cancellation_fee_pct", "late_return_fee_per_6h_pct",
     "tax_rate_pct", "min_driver_age", "max_driver_age", "included_miles_per_day",
     "excess_mile_fee", "fuel_refill_fee_per_gallon", "cancellation_window_hours",
     "release_date_days", "grace_period_hours", "turnaround_buffer_hours",
     "advance_deposit_pct",
    ].forEach((k) => { payload[k] = Number(payload[k]); });
    // Normalise booking_sources → clean, deduped, lowercase array of tags.
    payload.booking_sources = Array.isArray(settings.booking_sources)
      ? Array.from(new Set(
          settings.booking_sources
            .map((s) => (typeof s === "string" ? s.trim().toLowerCase() : ""))
            .filter(Boolean),
        ))
      : ["internet", "ai_agent", "direct", "agency", "hotel", "other"];
    try {
      const { data } = await api.put("/admin/settings", payload);
      setSettings(data);
      clearPublicSettingsCache();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e2) {
      setError(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return <p className="text-neutral-500 dark:text-neutral-400" data-testid="business-loading">Loading settings…</p>;
  }

  return (
    <div data-testid="admin-business-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 dark:text-neutral-400">Policies</p>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-2">
            Business <em className="italic">settings</em>.
          </h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-xl">
            Global deposit, taxes, mileage and fee policies. Changes apply to new bookings.
          </p>
        </div>
      </div>

      <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
        {/* v4.2.2 — Dynamic public branding. */}
        <Section title="Brand & Public Site">
          <Row>
            <Field label="Brand name">
              <Input
                value={settings.brand_name ?? ""}
                placeholder="Brainart Rent"
                onChange={(e) => update("brand_name", e.target.value)}
                data-testid="setting-brand-name"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Shown in the public navbar and footer on /book and /manage-booking.
              </p>
            </Field>
            <Field label="Operating region (internal)">
              <Input
                value={settings.operating_region ?? ""}
                placeholder="Brainart Master Demo"
                onChange={(e) => update("operating_region", e.target.value)}
                data-testid="setting-operating-region"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Admin-facing only — instance / region label used in reports and radar context.
                Not shown to visitors.
              </p>
            </Field>
          </Row>
          <Field label="Public hero title">
            <Input
              value={settings.public_hero_title ?? ""}
              placeholder="Book your premium ride in under a minute."
              onChange={(e) => update("public_hero_title", e.target.value)}
              data-testid="setting-public-hero-title"
            />
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
              Headline on the public booking engine hero section.
            </p>
          </Field>
        </Section>

        <Section title="Advance Deposit / Booking Confirmation Deposit">
          <Row>
            <Field label="Deposit required?">
              <Toggle checked={settings.deposit_required} onChange={(v) => update("deposit_required", v)} testId="setting-deposit-required" />
            </Field>
            <Field label="Deposit percentage (%)">
              <Input
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={settings.advance_deposit_pct ?? 30}
                onChange={(e) => update("advance_deposit_pct", e.target.value)}
                data-testid="setting-advance-deposit-pct"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Percentage of grand total required as upfront deposit. Remainder is marked as "Due at pickup".
              </p>
            </Field>
          </Row>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 leading-relaxed">
            The <strong>advance deposit</strong> is the % of the NET FINAL total
            (post-discount + extras) the customer must pay upfront to confirm the
            reservation. If they pay the full advance during checkout, the booking
            auto-confirms; otherwise it stays <em>pending payment</em> and the
            balance is due at pickup. The per-Car-Group refundable security deposit
            is configured separately in <em>Fleet → Vehicle</em>.
          </p>
        </Section>

        <Section title="Booking Rules">
          <Row>
            <Field label="Minimum lead time / release days (days)">
              <Input type="number" min={0} max={365}
                value={settings.release_date_days ?? 0}
                onChange={(e) => update("release_date_days", e.target.value)}
                data-testid="setting-release-days" />
            </Field>
            <Field label="Minimum rental duration (days)">
              <Input type="number" min={1} max={90}
                value={settings.min_rental_days ?? 1}
                onChange={(e) => update("min_rental_days", e.target.value)}
                data-testid="setting-min-rental-days" />
            </Field>
            <Field label="Grace period (hours)">
              <Input type="number" min={0} max={23}
                value={settings.grace_period_hours ?? 2}
                onChange={(e) => update("grace_period_hours", e.target.value)}
                data-testid="setting-grace-hours" />
            </Field>
            <Field label="Turnaround buffer (hours)">
              <Input type="number" min={0} max={72}
                value={settings.turnaround_buffer_hours ?? 3}
                onChange={(e) => update("turnaround_buffer_hours", e.target.value)}
                data-testid="setting-buffer-hours" />
            </Field>
          </Row>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 leading-relaxed">
            <strong>Lead time</strong>: customers must book at least this many days in
            advance. <strong>Grace period</strong>: extra hours past a 24h multiple
            that <em>don't</em> add another billable day (e.g. 24h+2h with a 3h grace
            = 1 day; 24h+4h = 2 days). <strong>Buffer</strong>: prep hours added
            around every confirmed booking so back-to-back overlaps are prevented.
          </p>
        </Section>

        <Section title="Taxes & Fees">
          <Row>
            <Field label="Tax rate (%)">
              <Input type="number" min={0} max={100} value={settings.tax_rate_pct} onChange={(e) => update("tax_rate_pct", e.target.value)} data-testid="setting-tax" />
            </Field>
            <Field label="Cancellation fee (% of total)">
              <Input type="number" min={0} max={100} value={settings.cancellation_fee_pct} onChange={(e) => update("cancellation_fee_pct", e.target.value)} />
            </Field>
            <Field label="Late return fee (% per 6h overdue)">
              <Input type="number" min={0} max={500} value={settings.late_return_fee_per_6h_pct} onChange={(e) => update("late_return_fee_per_6h_pct", e.target.value)} />
            </Field>
            <Field label="Cancellation window (hours)">
              <Input type="number" min={0} value={settings.cancellation_window_hours} onChange={(e) => update("cancellation_window_hours", e.target.value)} />
            </Field>
          </Row>
        </Section>

        <Section title="Mileage & Fuel">
          <Row>
            <Field label="Included miles per day">
              <Input type="number" min={0} value={settings.included_miles_per_day} onChange={(e) => update("included_miles_per_day", e.target.value)} data-testid="setting-miles" />
            </Field>
            <Field label="Excess mile fee (€/mile)">
              <Input type="number" step="0.01" value={settings.excess_mile_fee} onChange={(e) => update("excess_mile_fee", e.target.value)} />
            </Field>
            <Field label="Fuel refill fee (€/gallon)">
              <Input type="number" step="0.01" value={settings.fuel_refill_fee_per_gallon} onChange={(e) => update("fuel_refill_fee_per_gallon", e.target.value)} />
            </Field>
          </Row>
        </Section>

        <Section title="Eligibility">
          <Row>
            <Field label="Default minimum driver age">
              <Input type="number" min={18} max={99} value={settings.min_driver_age} onChange={(e) => update("min_driver_age", e.target.value)} data-testid="setting-default-min-age" />
            </Field>
            <Field label="Default maximum driver age">
              <Input type="number" min={30} max={99} value={settings.max_driver_age ?? 75} onChange={(e) => update("max_driver_age", e.target.value)} data-testid="setting-default-max-age" />
            </Field>
          </Row>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3">
            System-wide fallback age bounds when a Car Group / Vehicle has no explicit
            overrides. Per-vehicle overrides are set in the Fleet vehicle editor.
          </p>
        </Section>

        <Section title="Loyalty programme">
          <LoyaltyTiersEditor
            value={settings.loyalty_tiers || []}
            onChange={(next) => update("loyalty_tiers", next)}
          />
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 leading-relaxed">
            Repeat customers earn a tier once their <strong>completed</strong> booking
            count reaches the threshold. Tiers apply an automatic % discount on the
            reservation&apos;s taxable base. Voucher codes always win over the loyalty
            discount — only one discount applies at a time. Set the list to empty
            to switch the programme off.
          </p>
        </Section>

        <Section title="Company">
          <Row>
            <Field label="Company name">
              <Input value={settings.company_name} onChange={(e) => update("company_name", e.target.value)} data-testid="company-name" />
            </Field>
            <Field label="Support email">
              <Input type="email" value={settings.support_email} onChange={(e) => update("support_email", e.target.value)} />
            </Field>
            <Field label="Support phone">
              <Input value={settings.support_phone} onChange={(e) => update("support_phone", e.target.value)} />
            </Field>
          </Row>
          {/* v4.3.1 — identity printed on the PDF agreement header + footer. */}
          <Row>
            <Field label="Company address">
              <Input
                value={settings.company_address || ""}
                onChange={(e) => update("company_address", e.target.value)}
                placeholder="12 Nikis St, Chania 73100, Greece"
                data-testid="company-address"
              />
            </Field>
            <Field label="Company phone (documents)">
              <Input
                value={settings.company_phone || ""}
                onChange={(e) => update("company_phone", e.target.value)}
                placeholder="+30 28210 00000"
                data-testid="company-phone"
              />
            </Field>
            <Field label="VAT / Tax ID">
              <Input
                value={settings.company_vat || ""}
                onChange={(e) => update("company_vat", e.target.value)}
                placeholder="EL123456789"
                data-testid="company-vat"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Company logo URL">
              <Input
                value={settings.company_logo_url || ""}
                onChange={(e) => update("company_logo_url", e.target.value)}
                placeholder="https://yourdomain.com/logo.png"
                data-testid="company-logo-url"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Printed top-left on every PDF rental agreement. PNG or JPG, landscape works best.
              </p>
            </Field>
            <Field label="Logo preview">
              {settings.company_logo_url ? (
                <img
                  src={settings.company_logo_url}
                  alt="Company logo"
                  className="max-h-16 max-w-[220px] object-contain bg-white border border-black/10 dark:border-white/15 rounded p-2"
                  data-testid="company-logo-preview"
                />
              ) : (
                <p className="text-[11px] text-neutral-500 dark:text-neutral-400" data-testid="company-logo-preview-empty">
                  No logo set — the company name is printed instead.
                </p>
              )}
            </Field>
          </Row>
          <Row>
            <Field label="Public booking URL (View Site target)">
              <Input
                value={settings.public_booking_url || "/book"}
                onChange={(e) => update("public_booking_url", e.target.value)}
                placeholder="/book"
                data-testid="business-public-booking-url"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Relative path (/book) or absolute URL. Used by the top-bar “View site” button and by the Universal Search Bar embed.
              </p>
            </Field>
            <Field label="Embed code (Universal Search Bar)">
              <button
                type="button"
                onClick={() => setEmbedOpen(true)}
                className="text-sm px-4 py-2 rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-2"
                data-testid="business-open-embed"
              >
                Open embed generator →
              </button>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Copy-paste ready HTML/CSS/JS for Nicepage, WordPress, Webflow, or any site with an HTML block.
              </p>
            </Field>
          </Row>
        </Section>

        {/* v4.3.0 — Outgoing mail (SMTP) + instant test send. */}
        <Section title="Outgoing email (SMTP)">
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 -mt-1 mb-3">
            When an SMTP host is set, all notifications (customer confirmations with the agreement
            PDF attached, and internal booking alerts) are sent through it. Leave the host blank to
            fall back to the Resend API key, or to log-only mode when neither is configured.
          </p>
          <Row>
            <Field label="SMTP host">
              <Input
                value={settings.smtp_host || ""}
                onChange={(e) => update("smtp_host", e.target.value)}
                placeholder="smtp.yourmail.com"
                data-testid="smtp-host"
              />
            </Field>
            <Field label="Port">
              <Input
                type="number"
                value={settings.smtp_port ?? 587}
                onChange={(e) => update("smtp_port", e.target.value)}
                placeholder="587"
                data-testid="smtp-port"
              />
            </Field>
            <Field label="Username">
              <Input
                value={settings.smtp_user || ""}
                onChange={(e) => update("smtp_user", e.target.value)}
                placeholder="mailer@yourdomain.com"
                data-testid="smtp-user"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Password">
              <Input
                type="password"
                value={settings.smtp_password || ""}
                onChange={(e) => update("smtp_password", e.target.value)}
                placeholder={settings.smtp_password_set ? "•••••••• (saved — leave blank to keep)" : "SMTP password"}
                autoComplete="new-password"
                data-testid="smtp-password"
              />
            </Field>
            <Field label="Sender name">
              <Input
                value={settings.smtp_sender_name || ""}
                onChange={(e) => update("smtp_sender_name", e.target.value)}
                placeholder="Brainart Rent"
                data-testid="smtp-sender-name"
              />
            </Field>
            <Field label="Sender email (From)">
              <Input
                type="email"
                value={settings.smtp_sender_email || ""}
                onChange={(e) => update("smtp_sender_email", e.target.value)}
                placeholder="bookings@yourdomain.com"
                data-testid="smtp-sender-email"
              />
            </Field>
          </Row>
          <Row>
            <Field label="STARTTLS (port 587)">
              <Toggle
                checked={!!settings.smtp_use_tls}
                onChange={(v) => update("smtp_use_tls", v)}
                testId="smtp-use-tls"
              />
            </Field>
            <Field label="Implicit SSL (port 465)">
              <Toggle
                checked={!!settings.smtp_use_ssl}
                onChange={(v) => update("smtp_use_ssl", v)}
                testId="smtp-use-ssl"
              />
            </Field>
            <Field label="Booking Notifications Email (Primary)">
              <Input
                type="email"
                value={settings.admin_notification_email || ""}
                onChange={(e) => update("admin_notification_email", e.target.value)}
                placeholder="falls back to support email"
                data-testid="smtp-admin-inbox"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Booking Notifications CC Email (optional)">
              <Input
                value={settings.smtp_notification_cc || ""}
                onChange={(e) => update("smtp_notification_cc", e.target.value)}
                placeholder="owner@yourdomain.com, ops@yourdomain.com"
                data-testid="smtp-notification-cc"
              />
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Comma-separated. Copied on every new-booking notification.
              </p>
            </Field>
          </Row>
          <Row>
            <Field label="Send test email to">
              <div className="flex items-center gap-2">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="smtp-test-email-input"
                />
                <button
                  type="button"
                  onClick={sendTestEmail}
                  disabled={testing}
                  className="shrink-0 text-sm px-4 py-2 rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="smtp-test-email-btn"
                >
                  {testing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Send test
                </button>
              </div>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Save your settings first — the test uses the stored configuration.
              </p>
            </Field>
          </Row>
        </Section>

        {/* v4.3.2 — security & system health audit. */}
        <Section title="Security & system health">
          <div className="flex flex-wrap items-center gap-3 -mt-1 mb-3">
            <button
              type="button"
              onClick={runAudit}
              disabled={auditing}
              className="text-sm px-4 py-2 rounded bg-black text-white dark:bg-white dark:text-black inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="audit-run-btn"
            >
              {auditing ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
              Run Full Security &amp; Health Check
            </button>
            {audit && (
              <span
                className="text-xs inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-black/15 dark:border-white/20"
                data-testid="audit-overall"
              >
                <span className={`w-2 h-2 rounded-full ${AUDIT_DOT[audit.overall]}`} />
                {AUDIT_TEXT[audit.overall] || audit.overall}
                <span className="text-neutral-500">
                  · {audit.counts?.healthy || 0} ok / {audit.counts?.warning || 0} warn / {audit.counts?.critical || 0} critical
                </span>
              </span>
            )}
          </div>
          {audit ? (
            <>
              <div className="divide-y divide-black/8 dark:divide-white/10 border border-black/10 dark:border-white/10 rounded" data-testid="audit-results">
                {audit.checks.map((c) => (
                  <div key={c.name} className="flex items-start gap-3 px-4 py-3" data-testid={`audit-check-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${AUDIT_DOT[c.status]}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{c.name}</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 break-words">{c.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-2">
                Last run {new Date(audit.ran_at).toLocaleString()}
                {audit.report_sent_to?.length
                  ? ` · diagnostic report emailed to ${audit.report_sent_to.join(", ")}`
                  : audit.counts?.critical > 0
                    ? " · report email could not be delivered (check SMTP settings)"
                    : " · no email sent (auto-dispatch happens only on critical findings)"}
              </p>
            </>
          ) : (
            <p className="text-xs text-neutral-500 dark:text-neutral-400" data-testid="audit-empty">
              Checks database latency, pricing integrity, SMTP connectivity, document access tokens,
              environment &amp; security headers, demo accounts and payment gateway readiness.
            </p>
          )}
          <Row>
            <Field label="Audit report recipient">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <Input
                    type="email"
                    value={settings.security_audit_email || ""}
                    onChange={(e) => update("security_audit_email", e.target.value)}
                    placeholder="support@brainart.gr"
                    data-testid="audit-recipient"
                  />
                </div>
                <button
                  type="button"
                  onClick={sendAuditReport}
                  disabled={sendingAudit}
                  className="shrink-0 text-sm px-4 py-2 rounded border border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="audit-send-btn"
                >
                  {sendingAudit ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                  Send report to SysAdmin
                </button>
              </div>
              <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-1">
                Critical findings are emailed here automatically, with every active SysAdmin copied.
                Healthy and warning results are never auto-sent — use the button to share them on demand.
              </p>
            </Field>
          </Row>
        </Section>

        <Section title="Bank transfer beneficiary (public checkout)">
          <Row>
            <Field label="Bank name">
              <Input
                value={settings.bank_name || ""}
                onChange={(e) => update("bank_name", e.target.value)}
                placeholder="e.g. Eurobank / Alpha Bank / Piraeus / NBG"
                data-testid="business-bank-name"
              />
            </Field>
            <Field label="Beneficiary / account holder">
              <Input
                value={settings.bank_beneficiary || ""}
                onChange={(e) => update("bank_beneficiary", e.target.value)}
                placeholder="Brainart Creative Technologies MIKE"
                data-testid="business-bank-beneficiary"
              />
            </Field>
          </Row>
          <Row>
            <Field label="IBAN">
              <Input
                value={settings.bank_iban || ""}
                onChange={(e) => update("bank_iban", e.target.value)}
                placeholder="GRxx xxxx xxxx xxxx xxxx xxxx xxx"
                data-testid="business-bank-iban"
              />
            </Field>
            <Field label="SWIFT / BIC (optional)">
              <Input
                value={settings.bank_swift || ""}
                onChange={(e) => update("bank_swift", e.target.value)}
                placeholder="ERBKGRAA"
                data-testid="business-bank-swift"
              />
            </Field>
          </Row>
          <Row>
            <Field label="Transfer note (rendered under IBAN on the public modal + voucher)">
              <Input
                value={settings.bank_note || ""}
                onChange={(e) => update("bank_note", e.target.value)}
                placeholder="Please use your booking reference as the transfer description."
                data-testid="business-bank-note"
              />
            </Field>
          </Row>
        </Section>

        <SeasonsSection />

        <Section title="Email templates (customer confirmation · admin notification)">
          <EmailTemplatesSection />
        </Section>

        <BookingSourcesSection
          sources={settings.booking_sources}
          onChange={(next) => update("booking_sources", next)}
        />

        {error && <p className="text-red-600 dark:text-rose-400 text-sm" data-testid="business-error">{error}</p>}

        <div className="flex items-center gap-4 sticky bottom-4 bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 p-4 rounded shadow-lg">
          <p className="text-sm text-neutral-600 dark:text-neutral-400 flex-1">
            Changes are saved to your live business policy.
          </p>
          {saved && (
            <span className="inline-flex items-center gap-1 text-sm text-emerald-700 dark:text-emerald-400" data-testid="settings-saved">
              <Check size={14} /> Saved
            </span>
          )}
          <button
            type="submit"
            disabled={saving}
            className="bg-black text-white dark:bg-white dark:text-black px-6 py-2.5 text-sm hover:opacity-90 transition-opacity inline-flex items-center gap-2 disabled:opacity-50 rounded"
            data-testid="save-settings"
          >
            <Save size={14} /> {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>

      {embedOpen && (
        <UniversalWidgetModal
          targetUrl={settings.public_booking_url || "/book"}
          onClose={() => setEmbedOpen(false)}
        />
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-6">
      <h3 className="font-display text-xl mb-5">{title}</h3>
      {children}
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">{children}</div>;
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 dark:text-neutral-400 block mb-2">{label}</span>
      {children}
    </label>
  );
}

function Input(props) {
  return (
    <input
      {...props}
      className="w-full bg-transparent border-b border-black/15 dark:border-white/15 py-2 outline-none focus:border-black dark:focus:border-white transition-colors text-sm"
    />
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-black dark:bg-white" : "bg-neutral-300 dark:bg-neutral-700"}`}
      data-testid={testId}
      aria-pressed={checked}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full transition-transform ${checked ? "translate-x-5 bg-white dark:bg-black" : "translate-x-0 bg-white"}`}
      />
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Booking Sources — dynamic tag manager stored in business_settings.
// Admins add / rename / remove custom source tags that appear in the manual
// booking modal and the source filter on the Bookings page.
// ────────────────────────────────────────────────────────────────────────────
function BookingSourcesSection({ sources, onChange }) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(sources) ? sources : [];

  const add = () => {
    const clean = draft.trim().toLowerCase().replace(/\s+/g, "_");
    if (!clean) return;
    if (list.includes(clean)) return;
    onChange([...list, clean]);
    setDraft("");
  };

  const remove = (tag) => onChange(list.filter((s) => s !== tag));

  const rename = (idx, next) => {
    const clean = next.trim().toLowerCase().replace(/\s+/g, "_");
    if (!clean) return;
    const nextList = list.map((s, i) => (i === idx ? clean : s));
    onChange(Array.from(new Set(nextList)));
  };

  return (
    <div
      className="bg-white dark:bg-[#1F2937] border border-black/10 dark:border-white/10 rounded p-6"
      data-testid="booking-sources-section"
    >
      <h3 className="font-display text-xl mb-1">Booking Sources</h3>
      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4 max-w-2xl">
        Track where each reservation originated. These tags appear in the
        <em> New Reservation</em> modal and the Bookings filter. Built-in tags:
        <code className="mx-1">internet</code>,
        <code className="mx-1">ai_agent</code>,
        <code className="mx-1">direct</code>,
        <code className="mx-1">agency</code>,
        <code className="mx-1">hotel</code>,
        <code className="mx-1">other</code>. Add custom tags below.
      </p>

      <ul className="flex flex-wrap gap-2 mb-4" data-testid="booking-sources-list">
        {list.map((tag, idx) => (
          <li
            key={tag}
            className="flex items-center gap-1 border border-black/15 dark:border-white/15 bg-black/[0.02] dark:bg-white/[0.03] pl-3 pr-1 py-1 rounded"
            data-testid={`source-tag-${tag}`}
          >
            <input
              defaultValue={tag}
              onBlur={(e) => e.target.value !== tag && rename(idx, e.target.value)}
              className="bg-transparent text-sm outline-none w-32"
              aria-label={`Edit source ${tag}`}
            />
            <button
              type="button"
              onClick={() => remove(tag)}
              className="p-1 text-neutral-500 hover:text-rose-600 dark:hover:text-rose-400"
              data-testid={`remove-source-${tag}`}
              aria-label={`Remove source ${tag}`}
            >
              <X size={14} />
            </button>
          </li>
        ))}
        {list.length === 0 && (
          <li className="text-sm text-neutral-500 dark:text-neutral-400">
            No sources configured. Add one below.
          </li>
        )}
      </ul>

      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="e.g. viator, expedia, walk_in"
          className="flex-1 bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm"
          data-testid="new-source-input"
        />
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs bg-black text-white dark:bg-white dark:text-black hover:opacity-90 rounded"
          data-testid="add-source-btn"
        >
          <Plus size={12} /> Add source
        </button>
      </div>
    </div>
  );
}


/**
 * LoyaltyTiersEditor — CRUD grid for the `loyalty_tiers` array on
 * BusinessSettings. Each row = { name, min_bookings, discount_pct }.
 * Rows can be added/removed inline; validation happens on the outer form
 * submit (backend rejects zero-length names / negative pcts).
 */
function LoyaltyTiersEditor({ value = [], onChange }) {
  const update = (i, field, val) => {
    const next = value.map((row, idx) => (idx === i ? { ...row, [field]: val } : row));
    onChange(next);
  };
  const remove = (i) => onChange(value.filter((_, idx) => idx !== i));
  const add = () => onChange([
    ...value,
    { name: "", min_bookings: 0, discount_pct: 0 },
  ]);

  return (
    <div className="space-y-3" data-testid="loyalty-tiers-editor">
      <div className="grid grid-cols-[1fr_140px_140px_40px] gap-3 text-[10px] uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 px-1">
        <span>Tier name</span>
        <span>Min bookings</span>
        <span>Discount %</span>
        <span></span>
      </div>
      {value.length === 0 && (
        <p className="text-xs text-neutral-500 italic border border-dashed border-black/10 dark:border-white/10 p-3 rounded">
          No loyalty tiers configured. Add one to reward repeat customers.
        </p>
      )}
      {value.map((row, i) => (
        <div
          key={i}
          className="grid grid-cols-[1fr_140px_140px_40px] gap-3 items-center"
          data-testid={`loyalty-tier-row-${i}`}
        >
          <input
            value={row.name || ""}
            onChange={(e) => update(i, "name", e.target.value)}
            placeholder="Silver / Gold / Platinum"
            className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm"
            data-testid={`loyalty-tier-name-${i}`}
          />
          <input
            type="number" min={0} step={1}
            value={row.min_bookings ?? 0}
            onChange={(e) => update(i, "min_bookings", Number(e.target.value) || 0)}
            className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm text-right"
            data-testid={`loyalty-tier-min-${i}`}
          />
          <input
            type="number" min={0} max={100} step="0.1"
            value={row.discount_pct ?? 0}
            onChange={(e) => update(i, "discount_pct", Number(e.target.value) || 0)}
            className="bg-transparent border-b border-black/15 dark:border-white/15 py-1.5 outline-none focus:border-black dark:focus:border-white text-sm text-right"
            data-testid={`loyalty-tier-pct-${i}`}
          />
          <button
            type="button"
            onClick={() => remove(i)}
            className="text-rose-600 dark:text-rose-400 text-xs px-2 py-1 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded"
            data-testid={`loyalty-tier-remove-${i}`}
            aria-label="Remove tier"
          >
            <X size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="text-xs px-3 py-1.5 border border-dashed border-black/25 dark:border-white/25 hover:border-black dark:hover:border-white rounded inline-flex items-center gap-1"
        data-testid="loyalty-tier-add"
      >
        <Plus size={12} /> Add tier
      </button>
    </div>
  );
}


/**
 * CheckoutFieldsEditor — 3-status matrix for booking form fields.
 * Each field renders a segmented control (OFF / OPTIONAL / REQUIRED).
 */
const FIELD_GROUPS = [
  {
    label: "Customer identity",
    fields: [
      ["first_name", "First name"],
      ["last_name",  "Last name"],
      ["email",      "Email"],
      ["phone",      "Phone"],
      ["address",    "Address"],
      ["country",    "Country"],
      ["city",       "City"],
      ["zip",        "Zip / Postal code"],
      ["notes",      "Notes"],
    ],
  },
  {
    label: "Airport pickup / return",
    hint:  "Shown when a location's type = Airport.",
    fields: [
      ["airline_company", "Airline / carrier"],
      ["flight_number",   "Flight number"],
      ["flight_time",     "Flight time"],
    ],
  },
  {
    label: "Port / cruise pickup",
    hint:  "Shown when a location's type = Port.",
    fields: [
      ["vessel_company",      "Cruise line / operator"],
      ["vessel_name",         "Vessel name"],
      ["vessel_arrival_time", "Arrival time"],
    ],
  },
  {
    label: "Accommodation delivery",
    hint:  "Shown when a location's type = Hotel / Villa / Apartment.",
    fields: [
      ["accommodation_name",    "Hotel / villa / apartment name"],
      ["accommodation_address", "Accommodation address"],
    ],
  },
];

const STATUS_STYLES = {
  off: {
    active: "bg-neutral-700 text-white dark:bg-neutral-200 dark:text-neutral-900",
    idle:   "text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800",
  },
  optional: {
    active: "bg-amber-600 text-white",
    idle:   "text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20",
  },
  required: {
    active: "bg-emerald-700 text-white",
    idle:   "text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20",
  },
};

function CheckoutFieldsEditor({ value = {}, onChange }) {
  const setField = (key, status) => onChange({ ...value, [key]: status });

  return (
    <div className="space-y-6" data-testid="checkout-fields-editor">
      {FIELD_GROUPS.map((group) => (
        <div key={group.label}>
          <p className="text-xs uppercase tracking-[0.25em] text-neutral-500 dark:text-neutral-400">
            {group.label}
          </p>
          {group.hint && (
            <p className="text-[10px] text-neutral-500 dark:text-neutral-400 mt-1 italic">
              {group.hint}
            </p>
          )}
          <div className="mt-3 space-y-2">
            {group.fields.map(([key, label]) => {
              const current = value[key] || "off";
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-3"
                  data-testid={`cf-row-${key}`}
                >
                  <span className="text-sm">{label}</span>
                  <div className="inline-flex border border-black/15 dark:border-white/15 rounded overflow-hidden">
                    {["off", "optional", "required"].map((status) => {
                      const isActive = current === status;
                      const style = STATUS_STYLES[status];
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => setField(key, status)}
                          className={`px-3 py-1 text-[11px] uppercase tracking-wider transition-colors ${isActive ? style.active : style.idle}`}
                          data-testid={`cf-${key}-${status}`}
                        >
                          {status}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
